import React, { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Avatar,
  Chip,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Button,
} from '@mui/material';
import {
  TrendingUp,
  Home,
  People,
  Favorite,
  Notifications,
  Add,
  ArrowForward,
  Shield,
  ReportProblem,
  AccountBalance,
  Gavel,
  HistoryEdu,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { useAppStore } from '@/store/appStore';
import { ClientStatus } from '@/types';
import { DashboardMetrics } from '../types';

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { properties, clients, matches, notifications } = useAppStore();
  
  const [metrics, setMetrics] = useState<DashboardMetrics>({
    totalProperties: 0,
    activeProperties: 0,
    totalClients: 0,
    activeClients: 0,
    totalMatches: 0,
    successfulMatches: 0,
    conversionRate: 0,
    revenue: 0,
    monthlyGrowth: 0,
    topPerformingProperties: [],
    recentActivity: [],
  });

  useEffect(() => {
    // Calculate metrics from store data
    const activeProperties = properties.filter(p => p.isActive).length;
    const activeClients = clients.filter(c => c.status !== ClientStatus.INACTIVE).length;
    const successfulMatches = matches.filter(m => m.status === 'interested').length;
    const conversionRate = matches.length > 0 ? (successfulMatches / matches.length) * 100 : 0;

    setMetrics({
      totalProperties: properties.length,
      activeProperties,
      totalClients: clients.length,
      activeClients,
      totalMatches: matches.length,
      successfulMatches,
      conversionRate,
      revenue: 0, // This would come from a separate service
      monthlyGrowth: 12.5, // This would be calculated from historical data
      topPerformingProperties: properties
        .sort((a, b) => b.analytics.views - a.analytics.views)
        .slice(0, 3),
      recentActivity: [], // This would come from a separate service
    });
  }, [properties, clients, matches]);

  const StatCard: React.FC<{
    title: string;
    value: string | number;
    icon: React.ReactNode;
    color: string;
    trend?: number;
  }> = ({ title, value, icon, color, trend }) => (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography color="textSecondary" gutterBottom variant="h6">
              {title}
            </Typography>
            <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
              {value}
            </Typography>
            {trend !== undefined && (
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <TrendingUp sx={{ fontSize: 16, color: trend >= 0 ? 'green' : 'red', mr: 0.5 }} />
                <Typography variant="body2" color={trend >= 0 ? 'green' : 'red'}>
                  {trend >= 0 ? '+' : ''}{trend}%
                </Typography>
              </Box>
            )}
          </Box>
          <Avatar sx={{ bgcolor: color, width: 56, height: 56 }}>
            {icon}
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  );

  const QuickActionCard: React.FC<{
    title: string;
    description: string;
    icon: React.ReactNode;
    color: string;
    onClick: () => void;
  }> = ({ title, description, icon, color, onClick }) => (
    <Card sx={{ cursor: 'pointer', '&:hover': { boxShadow: 4 } }} onClick={onClick}>
      <CardContent sx={{ textAlign: 'center', p: 3 }}>
        <Avatar sx={{ bgcolor: color, width: 48, height: 48, mx: 'auto', mb: 2 }}>
          {icon}
        </Avatar>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>
      </CardContent>
    </Card>
  );

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
          Welcome back, {user?.profile.firstName}!
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Here's what's happening with your real estate business today.
        </Typography>
      </Box>

      {/* Stats Grid */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Properties"
            value={metrics.totalProperties}
            icon={<Home />}
            color="#1976d2"
            trend={metrics.monthlyGrowth}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Active Clients"
            value={metrics.activeClients}
            icon={<People />}
            color="#dc004e"
            trend={8.2}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Successful Matches"
            value={metrics.successfulMatches}
            icon={<Favorite />}
            color="#2e7d32"
            trend={15.3}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Conversion Rate"
            value={`${metrics.conversionRate.toFixed(1)}%`}
            icon={<TrendingUp />}
            color="#ed6c02"
            trend={-2.1}
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Quick Actions */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                Quick Actions
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                <QuickActionCard
                  title="Add Property"
                  description="List a new property"
                  icon={<Add />}
                  color="#1976d2"
                  onClick={() => navigate('/properties')}
                />
                <QuickActionCard
                  title="Add Client"
                  description="Register a new client"
                  icon={<People />}
                  color="#dc004e"
                  onClick={() => navigate('/clients')}
                />
                <QuickActionCard
                  title="View Matches"
                  description="Check property matches"
                  icon={<Favorite />}
                  color="#2e7d32"
                  onClick={() => navigate('/matches')}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Automatic Notary Sync */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                Automatic Notary Sync
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Alerts for Quitus Fiscal & ANCFCC Property Certificates requiring refresh.
              </Typography>
              <List>
                <ListItem divider>
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: 'warning.light', color: 'warning.dark', width: 32, height: 32 }}>
                      <Notifications />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary="Quitus Fiscal Expiring"
                    secondary="Villa 4, Les Jardins de l'Océan"
                    primaryTypographyProps={{ variant: 'body2', fontWeight: 'bold' }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                  <Button size="small" variant="outlined" color="warning">Renew</Button>
                </ListItem>
                <ListItem divider>
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: 'error.light', color: 'error.dark', width: 32, height: 32 }}>
                      <Notifications />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary="ANCFCC Update Required"
                    secondary="Apt 12, Résidence Anfa"
                    primaryTypographyProps={{ variant: 'body2', fontWeight: 'bold' }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                  <Button size="small" variant="outlined" color="error">Sync</Button>
                </ListItem>
                <ListItem>
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: 'success.light', color: 'success.dark', width: 32, height: 32 }}>
                      <Notifications />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary="Certificate Synced"
                    secondary="Apt 44, Marina Blanca"
                    primaryTypographyProps={{ variant: 'body2', fontWeight: 'bold' }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Top Performing Properties */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  Top Properties
                </Typography>
                <Button
                  size="small"
                  endIcon={<ArrowForward />}
                  onClick={() => navigate('/properties')}
                >
                  View All
                </Button>
              </Box>
              <List>
                {metrics.topPerformingProperties.map((property, index) => (
                  <ListItem key={property.id} divider={index < 2}>
                    <ListItemAvatar>
                      <Avatar
                        src={property.metadata.images[0]}
                        alt={property.metadata.title}
                        variant="rounded"
                      >
                        <Home />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={property.metadata.title}
                      secondary={`${property.analytics.views} views`}
                      primaryTypographyProps={{ variant: 'body2', fontWeight: 'medium' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                    <Chip
                      label={`$${property.metadata.price.toLocaleString()}`}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </ListItem>
                ))}
                {metrics.topPerformingProperties.length === 0 && (
                  <ListItem>
                    <ListItemText
                      primary="No properties yet"
                      secondary="Add your first property to get started"
                    />
                  </ListItem>
                )}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* 2026 Tax Shield & Compliance Center */}
      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid item xs={12}>
          <Card sx={{ border: '1px solid rgba(197, 160, 89, 0.2)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <Shield color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  2026 Tax Shield & Compliance Center
                </Typography>
              </Box>
              
              <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ReportProblem color="error" sx={{ fontSize: 20 }} />
                    High-Risk Cash Monitoring (Alerts)
                  </Typography>
                  <List disablePadding>
                    <ListItem 
                      sx={{ 
                        bgcolor: 'rgba(211, 47, 47, 0.05)', 
                        borderRadius: 1, 
                        mb: 1,
                        border: '1px solid rgba(211, 47, 47, 0.1)'
                      }}
                    >
                      <ListItemText 
                        primary="Transaction > 300,000 MAD Detected" 
                        secondary="Client: Mehdi Alaoui • Unit 501 • Potential 2% Cash Penalty"
                        primaryTypographyProps={{ variant: 'body2', fontWeight: 'bold', color: 'error.main' }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                      <Button size="small" variant="contained" color="error" sx={{ ml: 2 }}>Enforce Bank Transfer</Button>
                    </ListItem>
                  </List>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AccountBalance color="primary" sx={{ fontSize: 20 }} />
                    Project Readiness (Quitus & Permis)
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, textAlign: 'center' }}>
                        <Typography variant="h4" sx={{ fontWeight: 800, color: 'success.main', fontFamily: '"Playfair Display", serif' }}>12</Typography>
                        <Typography variant="caption" color="text.secondary">Ready Chantiers</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, textAlign: 'center' }}>
                        <Typography variant="h4" sx={{ fontWeight: 800, color: 'warning.main', fontFamily: '"Playfair Display", serif' }}>2</Typography>
                        <Typography variant="caption" color="text.secondary">Awaiting Quitus</Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Digital Notary Portal */}
      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid item xs={12}>
          <Card sx={{ bgcolor: 'rgba(60, 62, 63, 0.02)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Gavel color="primary" />
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    Digital Notary Portal (e-Signature)
                  </Typography>
                </Box>
                <Chip label="MA-E-SIGN Compliant" size="small" color="primary" variant="outlined" />
              </Box>
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Card variant="outlined">
                    <CardContent sx={{ textAlign: 'center', p: 3 }}>
                      <HistoryEdu sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                      <Typography variant="h6" sx={{ fontFamily: '"Playfair Display", serif' }}>Compromis de Vente</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
                        3 pending signatures from buyers.
                      </Typography>
                      <Button variant="contained" fullWidth size="small">Review & Sign</Button>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={8}>
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>Recent Notary Activities</Typography>
                  <List disablePadding>
                    <ListItem sx={{ borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
                      <ListItemText 
                        primary="Acte de Vente Finalized - Unit 204" 
                        secondary="Signed by Maitre Benjelloun • 2 hours ago" 
                      />
                      <Chip label="Completed" size="small" color="success" />
                    </ListItem>
                    <ListItem sx={{ borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
                      <ListItemText 
                        primary="Escrow Deposit Verified (10%)" 
                        secondary="Marina Blanca • Block A • 5 hours ago" 
                      />
                      <Chip label="Verified" size="small" color="success" />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Suspensive Conditions Lifted" 
                        secondary="Mortgage approved by Bank of Africa • 1 day ago" 
                      />
                      <Chip label="Updated" size="small" color="info" />
                    </ListItem>
                  </List>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DashboardPage;
